import React from 'react';
import { Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useTokens } from 'theme';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

/**
 * Surface metadata — shared across token badges, the query log, and analytics.
 *
 * A function of the tokens, not a constant: the tint is a wash under text in
 * the same hue, and on dark both must move or the badge reads dark-on-dark.
 */
export const channelMeta = (tokens) => ({
  cube:    { label: 'Evolve Cube', color: tokens.link, bg: tokens.infoBg, Icon: ViewInArIcon },
  trino:   { label: 'Trino',       color: tokens.warning, bg: tokens.warningBg, Icon: StorageIcon },
  mcp:     { label: 'MCP',         color: tokens.series[2], bg: tokens.raised, Icon: SmartToyIcon },
  console: { label: t('data.console'),     color: tokens.ink,     bg: tokens.raised,   Icon: TerminalIcon },
});

export function ChannelBadge({ channel }) {
  useTranslation();
  const themeTokens = useTokens();
  const meta = channelMeta(themeTokens)[channel] || { label: channel, color: themeTokens.inkMuted, bg: themeTokens.raised, Icon: null };
  const Icon = meta.Icon;
  return (
    <Chip
      size="small"
      label={meta.label}
      icon={Icon ? <Icon style={{ fontSize: 13 }} /> : undefined}
      sx={{
        bgcolor: meta.bg,
        color: meta.color,
        fontWeight: 600,
        '& .MuiChip-icon': { color: meta.color },
      }}
    />
  );
}

/**
 * Multi-select toggle for a token's allowed surfaces. Always keeps ≥1 selected.
 */
export function ChannelSelector({ value, onChange }) {
  useTranslation();
  return (
    <ToggleButtonGroup
      value={value}
      onChange={(_e, next) => { if (next && next.length) onChange(next); }}
      size="small"
      sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, gap: 0.75 } }}
    >
      {/* SEC-03: Trino is not selectable until per-tenant isolation exists. */}
      <ToggleButton value="cube"><ViewInArIcon sx={{ fontSize: 16 }} /> Evolve Cube</ToggleButton>
      <ToggleButton value="mcp"><SmartToyIcon sx={{ fontSize: 16 }} /> MCP</ToggleButton>
    </ToggleButtonGroup>
  );
}
